import { For, Show, createSignal } from "solid-js";

import { useWorkspaceUser, useWorkspaceUsers } from "~/lib/hooks";
import { formatUserName } from "~/lib/types";

type Props = {
  wsKey: () => string;
  // Selected user ID. Pass null/undefined for "no selection".
  value: () => string | null;
  // Called with the new user ID (or null when cleared) and its display name.
  onChange: (userId: string | null, name: string | null) => void;
  // Optional pre-fetched display name; UserPicker also auto-fetches if absent.
  selectedName?: () => string | null;
  label?: string;
  placeholder?: string;
  size?: "sm" | "md";
  disabled?: boolean;
};

export function UserPicker(props: Props) {
  const [query, setQuery] = createSignal("");
  const [open, setOpen] = createSignal(false);

  const users = useWorkspaceUsers(props.wsKey, query);

  // If the parent didn't pre-supply a name (e.g. page just loaded with a
  // persisted owner_user_id), fetch it once so the chip shows the person.
  const owner = useWorkspaceUser(props.wsKey, props.value);

  const size = () => props.size ?? "md";
  const inputCls = () =>
    size() === "sm" ? "input input-bordered input-sm w-full" : "input input-bordered w-full";

  const chipLabel = () => {
    const supplied = props.selectedName?.();
    if (supplied) return supplied;
    if (owner.data) return formatUserName(owner.data);
    return props.value()?.slice(0, 14) ?? "User";
  };

  return (
    <div class="relative">
      <Show
        when={props.value()}
        fallback={
          <div>
            <input
              type="text"
              class={inputCls()}
              placeholder={props.placeholder ?? "Search by name or email…"}
              aria-label={props.label ?? "Search users"}
              value={query()}
              disabled={props.disabled}
              onInput={(e) => {
                setQuery(e.currentTarget.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => window.setTimeout(() => setOpen(false), 150)}
              autocomplete="off"
            />
            <Show when={open()}>
              <ul
                role="listbox"
                aria-label="User matches"
                class="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded-lg bg-base-100 shadow-lg border border-base-300"
              >
                <For each={users.data?.items ?? []}>
                  {(u) => (
                    <li>
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        class="w-full text-left px-3 py-2 hover:bg-base-200"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          props.onChange(u.id, formatUserName(u));
                          setQuery("");
                          setOpen(false);
                        }}
                      >
                        <div class="font-medium">{formatUserName(u)}</div>
                        <div class="text-xs opacity-60">{u.email}</div>
                      </button>
                    </li>
                  )}
                </For>
                <Show when={(users.data?.items.length ?? 0) === 0 && !users.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">No users match.</li>
                </Show>
                <Show when={users.isLoading}>
                  <li class="px-3 py-2 text-sm opacity-60">Searching…</li>
                </Show>
              </ul>
            </Show>
          </div>
        }
      >
        <div
          class={`badge ${
            size() === "sm" ? "badge-md" : "badge-lg"
          } badge-secondary badge-outline gap-2`}
        >
          <span class="truncate max-w-xs">{chipLabel()}</span>
          <button
            type="button"
            class="btn btn-ghost btn-xs btn-circle"
            aria-label="Clear user"
            disabled={props.disabled}
            onClick={() => {
              props.onChange(null, null);
              setQuery("");
            }}
          >
            ✕
          </button>
        </div>
      </Show>
    </div>
  );
}
